import { AnalysisReferenceComponent } from '@shared/components/analysis-reference.component';
import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideCircleAlert,
  lucideCircleCheck,
  lucideExternalLink,
  lucideRotateCcw,
  lucideX,
} from '@ng-icons/lucide';
import { HlmBadgeDirective } from '@shared/spartan/ui-badge-helm/src';
import { AnalysisService } from '@shared/services/analysis.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { Analysis, TechnicalStage } from '@shared/models/analysis.model';
import {
  getExecutionLabel,
  getExecutionState,
  isExecutionRunning,
} from '@shared/models/analysis-status.util';
import {
  getStageConfigForTask,
  resolveTaskWindowForStage,
  PipelineStageConfig,
} from '@features/analyses/config/pipeline-stage.config';
import { ExecutionPreflightService } from '@shared/services/execution-preflight';
import { MissingRoofDataDialogService } from '@shared/services/missing-roof-data-dialog';
import { getErrorCodeMetadata } from '@shared/models/error-code.model';
import { relativeTime } from '@features/dashboard/utils/relative-time';

const PAGE_SIZE = 20;
/** Load the next page once the scroll position is this close to the end. */
const SCROLL_THRESHOLD_PX = 240;

/**
 * Retrying planning/placement into these just reproduces the same error —
 * there's nothing wrong with the template/media selection, detection simply
 * never produced any roof polygons to work with. Needs a real choice from
 * the user instead of a blind stage re-run.
 */
const NEEDS_DETECTION_ERROR_CODES = new Set([
  'MAX_PLACEMENT_NO_ROOF_AREAS',
  'PLANNING_NO_ROOF_POLYGONS',
]);

/** Maps a pipeline stage to the `technicalStage` enum value it corresponds to. */
const STAGE_ID_TO_TECHNICAL_STAGE: Record<PipelineStageConfig['stageId'], TechnicalStage> = {
  geocut: 'geocut',
  analyzed: 'analyzing',
  planned: 'planning',
};

@Component({
  selector: 'app-action-needed',
  standalone: true,
  imports: [AnalysisReferenceComponent, NgIconComponent, HlmBadgeDirective],
  providers: [
    provideIcons({
      lucideCircleAlert,
      lucideCircleCheck,
      lucideExternalLink,
      lucideRotateCcw,
      lucideX,
    }),
  ],
  templateUrl: './action-needed.html',
  styleUrl: './action-needed.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionNeededComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private analysisService = inject(AnalysisService);
  readonly currentOrgStore = inject(CurrentOrganizationStore);
  private preflight = inject(ExecutionPreflightService);
  private missingRoofDataDialog = inject(MissingRoofDataDialogService);

  private items = signal<Analysis[]>([]);
  private total = signal(0);
  private page = signal(0);
  private dismissedIds = signal<Set<string>>(new Set());
  private liveSubs = new Map<string, { unsubscribe: () => void }>();

  isInitialLoading = signal(true);
  isLoadingMore = signal(false);
  retryingIds = signal<Set<string>>(new Set());
  /**
   * Subset of `retryingIds` actually mid-network-call — a retry can sit for a
   * while just waiting on a dialog (media/template/roof-data choice), and the
   * spinner shouldn't run during that: nothing is "executing" yet.
   */
  private busyIds = signal<Set<string>>(new Set());

  totalFailed = this.total;
  hasMore = computed(() => this.items().length < this.total());

  visibleItems = computed(() => {
    const dismissed = this.dismissedIds();
    return this.items().filter((a) => !dismissed.has(a.analysisId));
  });

  async ngOnInit(): Promise<void> {
    await this.loadNextPage();
  }

  ngOnDestroy(): void {
    for (const sub of this.liveSubs.values()) sub.unsubscribe();
    this.liveSubs.clear();
  }

  private async loadNextPage(): Promise<void> {
    const orgId = this.currentOrgStore.organization()?.id;
    if (!orgId || this.isLoadingMore()) return;

    const nextPage = this.page() + 1;
    if (nextPage > 1) this.isLoadingMore.set(true);

    try {
      const { analyses, total } = await this.analysisService.getFailedAnalysesPage(
        orgId,
        nextPage,
        PAGE_SIZE,
      );
      this.items.update((current) => [...current, ...analyses]);
      this.total.set(total);
      this.page.set(nextPage);
    } catch (error) {
      console.error('[ActionNeeded] Failed to load failed analyses:', error);
    } finally {
      this.isInitialLoading.set(false);
      this.isLoadingMore.set(false);
    }
  }

  /** Bound to the row list's `(scroll)` — loads the next page near the end. */
  onScroll(event: Event): void {
    if (!this.hasMore() || this.isLoadingMore()) return;
    const el = event.target as HTMLElement;
    const distanceToEnd = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceToEnd < SCROLL_THRESHOLD_PX) {
      this.loadNextPage();
    }
  }

  executionLabel = getExecutionLabel;
  executionState = getExecutionState;
  isExecutionRunning = isExecutionRunning;

  /** Short German title for the failure, e.g. "Keine Dachflächen erkannt". */
  errorLabel(item: Analysis): string {
    const code = item.technicalErrorCode ?? item.technicalStatus?.errorCode;
    return code
      ? getErrorCodeMetadata(code).title
      : (item.technicalErrorMessage ??
        item.technicalStatus?.errorMessage ??
        'Unbekannter Fehler');
  }

  /** The long form, kept in the row's tooltip rather than on screen. */
  errorDetail(item: Analysis): string {
    return (
      item.technicalErrorDetail ??
      item.technicalStatus?.errorDetail ??
      item.technicalErrorMessage ??
      item.technicalStatus?.errorMessage ??
      ''
    );
  }

  changedAt(item: Analysis): string {
    const timestamp = item.updatedAt ?? item.createdAt;
    return timestamp ? relativeTime(new Date(timestamp).toISOString()) : '';
  }


  open(item: Analysis): void {
    const slug = this.currentOrgStore.organization()?.slug;
    if (slug) {
      this.router.navigate(['/organizations', slug, 'analyses', item.analysisId]);
    }
  }

  dismiss(analysisId: string): void {
    this.dismissedIds.update((set) => new Set(set).add(analysisId));
  }

  isRetrying(analysisId: string): boolean {
    return this.retryingIds().has(analysisId);
  }

  isBusy(analysisId: string): boolean {
    return this.busyIds().has(analysisId);
  }

  anyBusy = computed(() => this.busyIds().size > 0);

  private async withBusy<T>(analysisId: string, work: () => Promise<T>): Promise<T> {
    this.busyIds.update((set) => new Set(set).add(analysisId));
    try {
      return await work();
    } finally {
      this.busyIds.update((set) => {
        const next = new Set(set);
        next.delete(analysisId);
        return next;
      });
    }
  }

  /**
   * Sequential, not concurrent: the start-analysis/start-planning dialogs are
   * singleton components (one shared instance per service) — firing several
   * at once would have them stomp each other's inputs. Items that don't need
   * a dialog just resolve immediately, so this doesn't slow those down.
   */
  async retryAll(): Promise<void> {
    for (const item of this.visibleItems()) {
      if (!this.isRetrying(item.analysisId)) {
        await this.retry(item);
      }
    }
  }

  /**
   * Kick off the retry, then watch the analysis's own SSE stream so the card's
   * stage/state badge updates live as it re-runs — same event source the
   * analysis detail page uses — instead of guessing success from the retry
   * response alone. Stops watching once it reaches a terminal state
   * (completed → drop the card; failed again → leave it with the new error).
   *
   * Retries the FULL stage the failed task belongs to (SI rejects re-running
   * a single task in isolation mid-stage — e.g. task 3 alone without 2 — with
   * a 400; see `analysis-workflow.facade.ts`'s `resolveResumeStartTask`,
   * which this mirrors), not just the one failed task number.
   *
   * If the rerun range covers a stage that needs a selection — a file for
   * "analyzed" (tasks 2-3), a template for "planned" (tasks 4-5) — the shared
   * preflight asks for it (prefilled with the current choice) and persists it
   * on the SI analysis before anything is dispatched. Same gate the analysis
   * detail page uses.
   *
   * A "no roof areas" error is a special case: re-running planning/placement
   * would just reproduce it, since the actual problem is upstream (detection
   * never produced any polygons). That gets its own explanatory dialog
   * offering the real fixes — draw manually, or re-run detection — instead of
   * silently retrying into the same failure again.
   */
  async retry(item: Analysis): Promise<void> {
    if (!this.currentOrgStore.can('analysis', 'startAnalysis')) return;
    const orgId = this.currentOrgStore.organization()?.id;
    const analysisId = item.analysisId;
    if (!orgId || this.isRetrying(analysisId)) return;

    this.retryingIds.update((set) => new Set(set).add(analysisId));

    const errorCode = item.technicalErrorCode ?? item.technicalStatus?.errorCode;
    if (errorCode && NEEDS_DETECTION_ERROR_CODES.has(errorCode)) {
      const choice = await this.missingRoofDataDialog.confirm();

      if (choice === 'cancelled') {
        this.stopRetrying(analysisId);
        return;
      }
      if (choice === 'draw') {
        this.stopRetrying(analysisId);
        this.open(item);
        return;
      }

      // 'rerun-detection' — force the "analyzed" stage (tasks 2-3) regardless
      // of which later stage actually failed.
      const detectionStage = getStageConfigForTask(2)!;
      const detectionWindow = resolveTaskWindowForStage(detectionStage, 'rerun', 2);
      try {
        await this.withBusy(analysisId, () =>
          this.analysisService.executeAnalysisTasks(orgId, analysisId, {
            start_task: detectionWindow.startTask,
            end_task: detectionWindow.endTask,
          }),
        );
      } catch (error) {
        console.error('[ActionNeeded] Detection re-run failed:', error);
        this.stopRetrying(analysisId);
        return;
      }
      this.watchLive(orgId, analysisId, 'analyzing');
      return;
    }

    const currentTask = item.technicalStatus?.task ?? 1;
    const stageConfig = getStageConfigForTask(currentTask);
    const window = stageConfig
      ? resolveTaskWindowForStage(stageConfig, 'rerun', currentTask)
      : { startTask: currentTask, endTask: currentTask };

    try {
      const confirmed = await this.preflight.confirm(item, orgId, window);
      if (!confirmed) {
        this.stopRetrying(analysisId);
        return;
      }

      await this.withBusy(analysisId, () =>
        this.analysisService.executeAnalysisTasks(orgId, analysisId, {
          start_task: window.startTask,
          end_task: window.endTask,
        }),
      );
    } catch (error) {
      console.error('[ActionNeeded] Retry request failed:', error);
      this.stopRetrying(analysisId);
      return;
    }

    this.watchLive(
      orgId,
      analysisId,
      stageConfig ? STAGE_ID_TO_TECHNICAL_STAGE[stageConfig.stageId] : undefined,
    );
  }

  /**
   * Optimistic flip to "queued" — the execute call already transitioned the
   * analysis server-side by the time we get here, but `streamAnalysisEvents`
   * only pushes events for CHANGES from this point forward, not a snapshot
   * of current state. Without this the card would keep showing the old
   * "Fehler" badge until the *next* transition (e.g. queued → processing),
   * silently skipping the one the user actually wants to see. Then watches
   * live until a terminal state is reached.
   */
  private watchLive(orgId: string, analysisId: string, stage?: TechnicalStage): void {
    this.patchItem(analysisId, {
      technicalState: 'queued',
      ...(stage ? { technicalStage: stage } : {}),
    });

    const subscription = this.analysisService
      .streamAnalysisEvents(orgId, analysisId)
      .subscribe({
        next: (updated) => {
          // The SSE payload only ever carries the NESTED `technicalStatus`
          // blob (no flat `technicalState`/`technicalStage`) — reading
          // `updated.technicalState` directly is always undefined, so every
          // event silently fell into the "still running" branch below and
          // the flat fields we optimistically set never got overwritten,
          // leaving the card stuck on "Warteschlange" forever (even after a
          // real failure) until a full page reload re-fetched flat fields.
          const state = getExecutionState(updated);

          // The payload has no `stage` field either (only `task`/`taskKey`),
          // so `getExecutionStage` would fall back to its 'idle' default and
          // wipe out the correct stage we set optimistically in `watchLive` —
          // that's why the badge lost its " · Analyse"/" · Geodaten" suffix.
          // Derive the real stage from the task number instead, and only
          // touch `technicalStage` when that actually resolves to something.
          const taskNo = updated.technicalStatus?.task;
          const stageForUpdate =
            taskNo != null ? getStageConfigForTask(taskNo) : undefined;
          const stage = stageForUpdate
            ? STAGE_ID_TO_TECHNICAL_STAGE[stageForUpdate.stageId]
            : undefined;

          const patch: Partial<Analysis> = {
            technicalState: state as Analysis['technicalState'],
            ...(stage ? { technicalStage: stage } : {}),
            technicalStatus: updated.technicalStatus,
            technicalErrorCode: updated.technicalStatus?.errorCode ?? updated.technicalErrorCode,
            technicalErrorMessage:
              updated.technicalStatus?.errorMessage ?? updated.technicalErrorMessage,
            technicalErrorDetail:
              updated.technicalStatus?.errorDetail ?? updated.technicalErrorDetail,
          };

          if (state === 'completed') {
            this.removeItem(analysisId);
            this.stopRetrying(analysisId);
            subscription.unsubscribe();
            this.liveSubs.delete(analysisId);
          } else if (state === 'failed') {
            this.patchItem(analysisId, patch);
            this.stopRetrying(analysisId);
            subscription.unsubscribe();
            this.liveSubs.delete(analysisId);
          } else {
            // still queued/processing — reflect live stage on the card, keep watching
            this.patchItem(analysisId, patch);
          }
        },
        error: () => this.stopRetrying(analysisId),
      });

    this.liveSubs.set(analysisId, subscription);
  }

  private stopRetrying(analysisId: string): void {
    this.retryingIds.update((set) => {
      const next = new Set(set);
      next.delete(analysisId);
      return next;
    });
  }

  private patchItem(analysisId: string, patch: Partial<Analysis>): void {
    this.items.update((items) =>
      items.map((a) => (a.analysisId === analysisId ? { ...a, ...patch } : a)),
    );
  }

  /** Item resolved (no longer failed) — drop it and correct the total so the badge count stays right. */
  private removeItem(analysisId: string): void {
    this.items.update((items) => items.filter((a) => a.analysisId !== analysisId));
    this.total.update((n) => Math.max(0, n - 1));
  }
}
